// External dependencies
import { connect } from 'react-redux';
import { get, isEmpty } from 'lodash';
import i18next from 'i18next';
import React, { useContext, useEffect, useMemo, useState, useRef } from 'react';

// Actions
import {
  closeGenericModal,
  openGenericModal,
  openMediumModal,
  openSmallModal,
  refreshGenericModal,
} from '@actions/modal';
import { loading, loadingSuccess } from '@actions/loading';
import { showErrorMessage, showSuccessMessage } from '@actions/messageconfirmation';

// Commons
import { Button, ListView } from '@commons/utils/styledLibraryComponents';
import { CATEGORY_TYPES_OBJECT, SUB_CATEGORY_TYPES_OBJECT } from '@commons/constants/categoryTypes';
import { handleOpenedImportFileModal } from '@commons/Modals/ImportModal/utils/config';
import { IMPORT_STATE, IMPORT_TYPE, SWITCH_STEP } from '@commons/Modals/ImportModal/utils/constant';
import { isUserManager, userHasRoleInpulse } from '@commons/utils/roles';
import EmptyState from '@commons/EmptyState';
import GeneralEmptyStateListView from '@commons/GeneralEmptyStateListView';
import NavigationBreadCrumb from '@commons/Breadcrumb/NavigationBreadCrumb';
import paginationUtils from '@commons/utils/listViewPagination';

// Contexts
import { CentralKitchenProductContext } from '@context/CentralKitchenProductContext';
import { getListViewPersistentFilters, PersistentFilterTypes } from '@context/utils';
import { ProductContext } from '@context/ProductContext';

// Selectors
import { canEditExternalClient } from '@selectors/actions/externalClientActions';
import { EXTERNAL_FEATURE_PATHS } from '@commons/utils/featurePaths';
import { getAuthorizedActions, getHasRecipeVariants } from '@selectors/featureProps';
import { getCentralKitchenStores, getSalesPointStores } from '@selectors/stores';
import { getClientBrandsWithoutNone, getClientInfo } from '@selectors/client';

// Services
import { externalClient as externalClientService } from '@services/externalClient';
import { fetchProductsCategoriesOfClient } from './detail/components/ProductInfo/common/services';
import { product as productService } from '@services/product';
import centralService from '@services/central';
import clientService from '@services/client';

// Hooks
import { ENUM_QUERY_PARAMS, useListViewQueryParams } from '@hooks/useListViewQueryParams';
import { useCategoriesBatchUpdateModal } from '@hooks/useCategoriesBatchUpdateModal';

// Utils
import { CHOICES_DROPDOWN_ACTIVE } from '@admin/utils/DropdownItems';
import { downloadFile, formatXlsFileToJson } from '@backoffice/utils';
import { formatCentralKitchenProductBatchCreationFile } from './utils/formatCentralKitchenProductBatchCreationFile';
import { formatProductBatchUpdateOrCreationFile } from './utils/formatProductsBatchCreationFile';
import { getActions, getEmptyStateActions, getRowActions } from './utils/getActions';
import {
  getCentralKitchenProductsColumns,
  getColumns,
  getColumnsFilterList,
} from './utils/columns';
import { getClientStoreNameTranslation } from '@commons/utils/translations';
import {
  getEnableProductsModalParams,
  getManageB2bCatalogsModalParams,
} from './utils/modalConfigurations';
import { getImportErrorFileConfig } from './utils/importErrorFile';
import { getIsInCentralKitchenOnlyPage } from '@admin/utils/centralKitchen';
import { getUpdateProductModalConfig } from './utils/updateModaleConfig';
import { IMPORT_MODAL_STEPS } from '@commons/Modals/MultiStepModal';
import utils from './detail/components/ProductInfo/utils';

// Design
import { ListViewContainer } from './styledComponents';

// Components
import CentralKitchenProductExport from '@admin/KitchenProducts/KitchenProductsProducts/components/CentralKitchenProductExport';
import DeepsightFiltersButton from '@admin/components/FilterButton';

// Modals
import {
  ENUM_B2B_CATALOGS_UPDATE_MODE,
  getB2bCatalogsUpdateModeChoices,
} from './components/ManageB2bCatalogsModal';
import CreateProductModal from './components/CreateProductModal';
import DeleteProductsModal from './components/DeleteProductsModal';
import EnableProductModal, {
  enableProductMapCashierProductsAndAddProductionParameters,
} from './components/EnableProductModal';
import ExportProductByStoreProgressModal from './components/ExportProductByStoreProgressModal';
import IncoExportModal from './components/IncoExportModal';
import ProductExportModal from './components/ProductExportModal';

// Export-by-store (shared with the recipe export-by-store)
import { getExportByStoreModalConfig } from '@admin/products/recipes/utils/exportByStoreModalConfig';
import { getFilteredStoresWithSelectedGroups } from '@commons/ExportModalWithStoresAndPeriod/utils';
import { getImportCentralKitchenProductsScopeModalConfig } from './utils/importCentralKitchenProductsScopeModalConfig';
import { getSelectableGroupsFromStores } from '@admin/products/recipes/utils/storeGroups';

import { getTheme } from '@commons/utils/theme';

// The client dropdown of the "Modifier les catalogues client" modal is not paginated
const B2B_CATALOGS_CLIENTS_MAX_PER_PAGE = 1000;

export const Products = ({
  history,
  match: { path },
  // Redux props
  user,
  modal,
  currency,
  clientBrands,
  authorizedActions,
  activeStores,
  hasVariants,
  canManageB2bCatalogs,
  client: {
    clientId,
    storeName,
    areProductsSync,
    hasMultipleBrands,
    hasMultipleChannels,
    hasSimpleModifierManagement,
    clientChannels,
    defaultChannelId,
  },
  // Redux actions
  openModal,
  pageLoaded,
  pageLoading,
  openGenericModal,
  showErrorMessage,
  closeGenericModal,
  showSuccessMessage,
  refreshGenericModal,
  openModalExportInfo,
}) => {
  /***********/
  /* States */
  /**********/

  const theme = getTheme();

  const userLanguageCode = get(user, 'lnkLanguageAccountrel.code', 'fr');

  const contextHandler =
    useContext(getIsInCentralKitchenOnlyPage() ? CentralKitchenProductContext : ProductContext) ||
    {};

  const {
    selectedStatus,
    setSelectedStatus,
    advancedFilters,
    setAdvancedFilters,
    customParams,
    setCustomParams,
    search,
  } = contextHandler;

  const persistentFilters = isEmpty(contextHandler)
    ? []
    : getListViewPersistentFilters(contextHandler);

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialFetch, setIsInitialFetch] = useState(true);

  // Categories
  const [productsCategories, setProductsCategories] = useState([]);
  const [productsSubCategories, setProductsSubCategories] = useState([]);

  // Data states
  const [products, setProducts] = useState([]);
  const [totalProducts, setTotalProducts] = useState(0);

  // List View
  const [columns, setColumns] = useState([]);
  const [actions, setActions] = useState([]);
  const [rowActions, setRowActions] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [listViewQueryParams, setListViewQueryParams] = useListViewQueryParams(false);
  const [paginationProps, setPaginationProps] = useState(null);

  // Filter states
  const [filters, setFilters] = useState(null);
  const [applyFilters, setApplyFilters] = useState(false);
  const [columnsFilterList, setColumnsFilterList] = useState([]);

  // Render states
  const [emptyState, setEmptyState] = useState(false);
  const [emptyStateActions, setEmptyStateActions] = useState([]);
  const [isInCentralKitchenOnlyPage] = useState(getIsInCentralKitchenOnlyPage());

  // Export-by-store modal state
  const [isExportByStoreModalOpen, setIsExportByStoreModalOpen] = useState(false);
  const [exportByStoreGroups, setExportByStoreGroups] = useState([]);
  const [exportByStoreSelectedGroups, setExportByStoreSelectedGroups] = useState([]);
  const [exportByStoreStores, setExportByStoreStores] = useState([]);
  const [exportByStoreSelectedStores, setExportByStoreSelectedStores] = useState([]);
  // Products selected when the export-by-store action was triggered, forwarded to the progress modal
  const exportByStoreProductsRef = useRef([]);

  // "Modifier les catalogues client" modal state (bulk add / remove to B2B catalogs)
  const [isManageB2bCatalogsModalOpen, setIsManageB2bCatalogsModalOpen] = useState(false);
  const [b2bCatalogsClients, setB2bCatalogsClients] = useState([]);
  const [b2bCatalogsSelectedClients, setB2bCatalogsSelectedClients] = useState([]);
  const [b2bCatalogsSelectedMode, setB2bCatalogsSelectedMode] = useState(null);
  // Products selected when the action was triggered, forwarded to the save handler
  const manageB2bCatalogsProductsRef = useRef([]);

  // Sales-point stores (no central kitchens), groups always an array — guards the group filtering
  const salesPointStores = useMemo(
    () =>
      getSalesPointStores(activeStores || []).map((store) => ({
        ...store,
        groups: store.groups ?? [],
      })),
    [activeStores],
  );

  // Central kitchen stores, groups always an array — guards the group filtering
  const centralKitchenStores = useMemo(
    () =>
      getCentralKitchenStores(activeStores || []).map((store) => ({
        ...store,
        groups: store.groups ?? [],
      })),
    [activeStores],
  );

  // Action "import products" states
  const [errorFile, setErrorFile] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [currentStep, setCurrentStep] = useState(IMPORT_STATE.SELECT_FILE);
  const [batchProductsToCreate, setBatchProductsToCreate] = useState(null);

  // Central kitchens on which the imported products will be created
  const [isImportScopeModalOpen, setIsImportScopeModalOpen] = useState(false);
  const [importScopeGroups, setImportScopeGroups] = useState([]);
  const [importScopeSelectedGroups, setImportScopeSelectedGroups] = useState([]);
  const [importScopeStores, setImportScopeStores] = useState([]);
  const [importScopeSelectedStores, setImportScopeSelectedStores] = useState([]);

  // Action "enable product" states
  const [enablingProduct, setEnablingProduct] = useState(null);

  const listViewRef = useRef();
  const hasInitializedFromUrl = useRef(false);

  /* METHODS */

  const getProducts = async () => {
    setIsLoading(true);

    try {
      const columnPropertyKeys = columns.map((column) => column.propertyKey);

      const { search, skip, limit, orderBy, orderType } = paginationUtils.getPayloadPagination({
        columnPropertyKeys,
        listViewQueryParams,
      });

      // Convert skip back to page for the API (API expects page starting from 1)
      const page = Math.floor(skip / limit) + 1;

      const { data, count } = await productService.getPaginatedProductsOfClient(
        {
          orderBy,
          orderType,
          page,
          limit,
          search: search || '',
        },
        {
          brandId: customParams.brandId,
          active: { eq: get(selectedStatus, 'filterValue', false) },
          categoryId: customParams.categoryId,
          isIngredient: customParams.isIngredient,
          subCategoryId: customParams.subCategoryId,
          priceWithTaxes: customParams.priceWithTaxes,
          nbCashierProductMappingCount: customParams.nbCashierProductMappingCount,
          deliveredStoreCount: customParams.deliveredStoreCount,
          isKitchen: { eq: isInCentralKitchenOnlyPage },
        },
      );

      if (!data || !data.length) {
        setProducts([]);
        setTotalProducts(0);

        if (isInitialFetch) {
          setEmptyState(true);
        }

        return;
      }

      const formattedProducts = data.map((product) => ({
        id: product.id,
        sku: product.sku,
        name: product.name,
        active: product.active,
        isKitchen: product.isKitchen,
        entity: product.entity,
        brand: product.brand || '',
        priceWithTaxes: product.priceWithTaxes || '',
        priceHT: utils.getProductPriceExcludingVAT(product, defaultChannelId) || '',
        vatRate: product.vatRate,
        currency: product.currency,
        shelfLife: product.shelfLife,
        img: product.img,
        ean: product.ean,
        deliveryVatRate: product.deliveryVatRate,
        deliveryPriceWithTaxes: product.deliveryPriceWithTaxes,
        portionSizeInGrams: product.portionSizeInGrams,
        isIngredient: get(product, 'entity.isIngredient', false),
        category: product.category || i18next.t('GENERAL.NONE_VALUE'),
        subCategory: product.subCategory || i18next.t('GENERAL.NONE_VALUE'),
        nbCashierProductMappingCount: product.nbCashierProductMappingCount || 0,
        nbCashierProductMappingActive: product.nbCashierProductMappingActive || 0,
        deliveredStoreCount: product.deliveredStoreCount || 0,
        productSupplierProductMappings: product.productSupplierProductMappings || [],
        isChild: product.parentCashierProducts?.length > 0,
      }));

      setProducts(formattedProducts);
      setTotalProducts(count);
    } catch {
      showErrorMessage(i18next.t('GENERAL.FETCH_CLIENT_PRODUCTS_FAILURE'));

      setProducts([]);
      setTotalProducts(0);
    } finally {
      setIsLoading(false);
      setIsInitialFetch(false);
    }
  };

  // Must be declared after getProducts
  const categoriesBatchUpdateModal = useCategoriesBatchUpdateModal({
    onRefresh: getProducts,
    loadProps: {
      clientId,
      type: isInCentralKitchenOnlyPage
        ? CATEGORY_TYPES_OBJECT.CENTRAL_KITCHEN_PRODUCT
        : CATEGORY_TYPES_OBJECT.PRODUCT,
      propertyType: 'categories',
    },
    itemListSorterParams: {
      disableMergeItems: true,
      disableCreateItem: false,
      entityName: i18next.t('FEATURE.ADMIN.PRODUCTS'),
      createItemLabel: i18next.t('ADMIN.GENERAL.MANAGE_CATEGORIES_CREATE_LABEL'),
    },
  });

  const subCategoriesBatchUpdateModal = useCategoriesBatchUpdateModal({
    onRefresh: getProducts,
    loadProps: {
      clientId,
      type: isInCentralKitchenOnlyPage
        ? SUB_CATEGORY_TYPES_OBJECT.CENTRAL_KITCHEN_PRODUCT
        : SUB_CATEGORY_TYPES_OBJECT.PRODUCT,
      propertyType: 'subCategories',
    },
    itemListSorterParams: {
      disableMergeItems: true,
      disableCreateItem: false,
      entityName: i18next.t('FEATURE.ADMIN.PRODUCTS'),
      createItemLabel: i18next.t('ADMIN.GENERAL.MANAGE_SUB_CATEGORIES_CREATE_LABEL'),
    },
  });

  /**********************/
  /* Go to details page */
  /**********************/

  const goProductDetailsPage = (product) => {
    if (isInCentralKitchenOnlyPage) {
      history.push({
        pathname: `/admin/kitchen-products/products/${product.id}/details`,
      });

      return;
    }

    history.push({
      pathname: `/admin/products/${product.id}/details`,
    });
  };

  /**************************/
  /* Create Product methods */
  /**************************/

  const triggerCreate = () => {
    if (isInCentralKitchenOnlyPage) {
      history.push({
        pathname: '/admin/kitchen-products/products/create',
      });

      return;
    }

    const params = {
      component: CreateProductModal,
      history,
      clientCurrency: currency,
    };

    return openModal(params);
  };

  /**************************/
  /* Export Product methods */
  /**************************/

  const triggerExport = async (items) => {
    if (isInCentralKitchenOnlyPage) {
      openModalExportInfo({
        component: CentralKitchenProductExport,
        title: i18next.t('ADMIN.PRODUCTS.EXPORT'),
        products: items || [], // Pass selected products if any
        customParams: items && items.length > 0 ? null : customParams, // Only pass filters if no selection
        selectedStatus: items && items.length > 0 ? null : selectedStatus, // Only pass status if no selection
      });
      return;
    }

    // Use ProductExportModal for both cases: selected items or all products by filters
    openModalExportInfo({
      component: ProductExportModal,
      title: i18next.t('ADMIN.PRODUCTS.EXPORT'),
      products: items || [], // Pass selected products if any
      customParams: items && items.length > 0 ? null : customParams, // Only pass filters if no selection
      selectedStatus: items && items.length > 0 ? null : selectedStatus, // Only pass status if no selection
    });
  };

  const handleExportByStoreCloseCleanUp = () => {
    setIsExportByStoreModalOpen(false);
    setExportByStoreGroups([]);
    setExportByStoreSelectedGroups([]);
    setExportByStoreStores([]);
    setExportByStoreSelectedStores([]);
    exportByStoreProductsRef.current = [];
  };

  const handleExportByStore = () => {
    // Capture before the cleanup setters are queued so the values are unambiguous
    const exportedProducts = exportByStoreProductsRef.current;
    const selectedStores = exportByStoreSelectedStores;

    handleExportByStoreCloseCleanUp();
    closeGenericModal();

    openModalExportInfo({
      component: ExportProductByStoreProgressModal,
      title: i18next.t('ADMIN.RECIPES.EXPORT_BY_STORE_MODAL_TITLE'),
      selectedStores,
      products: exportedProducts,
      customParams: exportedProducts.length ? null : customParams,
      selectedStatus: exportedProducts.length ? null : selectedStatus,
    });
  };

  const triggerExportByStore = (items) => {
    exportByStoreProductsRef.current = items || [];

    const allGroups = getSelectableGroupsFromStores(salesPointStores);
    const initialStores = allGroups.length
      ? getFilteredStoresWithSelectedGroups(allGroups, salesPointStores)
      : salesPointStores;

    setExportByStoreGroups(allGroups);
    setExportByStoreSelectedGroups(allGroups);
    setExportByStoreStores(initialStores);
    setExportByStoreSelectedStores(initialStores);
    setIsExportByStoreModalOpen(true);

    openGenericModal(
      getExportByStoreModalConfig({
        groups: allGroups,
        selectedGroups: allGroups,
        setSelectedGroups: setExportByStoreSelectedGroups,
        stores: initialStores,
        selectedStores: initialStores,
        setSelectedStores: setExportByStoreSelectedStores,
        handleExport: handleExportByStore,
        handleCloseCleanUp: handleExportByStoreCloseCleanUp,
      }),
    );
  };

  // When selected groups change, recompute filtered stores and drop deselected ones
  useEffect(() => {
    if (!isExportByStoreModalOpen) {
      return;
    }

    const filteredStores = exportByStoreSelectedGroups.length
      ? getFilteredStoresWithSelectedGroups(exportByStoreSelectedGroups, salesPointStores)
      : salesPointStores;

    const filteredStoreIds = new Set(filteredStores.map(({ id }) => id));

    setExportByStoreStores(filteredStores);
    setExportByStoreSelectedStores((prev) => prev.filter(({ id }) => filteredStoreIds.has(id)));
  }, [exportByStoreSelectedGroups, isExportByStoreModalOpen, salesPointStores]);

  // Refresh the export-by-store selection modal whenever its selection state changes
  useEffect(() => {
    if (!isExportByStoreModalOpen) {
      return;
    }

    refreshGenericModal(
      getExportByStoreModalConfig({
        groups: exportByStoreGroups,
        selectedGroups: exportByStoreSelectedGroups,
        setSelectedGroups: setExportByStoreSelectedGroups,
        stores: exportByStoreStores,
        selectedStores: exportByStoreSelectedStores,
        setSelectedStores: setExportByStoreSelectedStores,
        handleExport: handleExportByStore,
        handleCloseCleanUp: handleExportByStoreCloseCleanUp,
      }),
    );
  }, [
    isExportByStoreModalOpen,
    exportByStoreGroups,
    exportByStoreSelectedGroups,
    exportByStoreStores,
    exportByStoreSelectedStores,
  ]);

  const triggerExportIncoData = (items) => {
    openModalExportInfo({
      component: IncoExportModal,
      title: i18next.t('ADMIN.PRODUCTS.EXPORT_INCO_DATA'),
      products: items || [], // Pass selected products if any
      customParams: items && items.length > 0 ? null : customParams, // Only pass filters if no selection
      selectedStatus: items && items.length > 0 ? null : selectedStatus, // Only pass status if no selection
    });
  };

  /*****************************************/
  /* Manage B2B catalogs methods (COR-1677) */
  /*****************************************/

  // Clients scope: central kitchens on the central products page, sales points otherwise
  const b2bCatalogsScopedStoreIds = useMemo(
    () =>
      (activeStores || [])
        .filter(({ isKitchen }) => !!isKitchen === isInCentralKitchenOnlyPage)
        .map(({ id }) => id),
    [activeStores, isInCentralKitchenOnlyPage],
  );

  const handleManageB2bCatalogsCloseCleanUp = () => {
    setIsManageB2bCatalogsModalOpen(false);
    setB2bCatalogsClients([]);
    setB2bCatalogsSelectedClients([]);
    manageB2bCatalogsProductsRef.current = [];
  };

  const handleManageB2bCatalogsSave = async () => {
    const productIds = manageB2bCatalogsProductsRef.current.map(({ id }) => id);
    const externalClientIds = b2bCatalogsSelectedClients.map(({ id }) => id);
    const isAddMode = b2bCatalogsSelectedMode?.key === ENUM_B2B_CATALOGS_UPDATE_MODE.ADD;

    handleManageB2bCatalogsCloseCleanUp();

    try {
      if (isAddMode) {
        await externalClientService.addProductsToCatalogs(externalClientIds, productIds);

        showSuccessMessage(i18next.t('ADMIN.PRODUCTS.B2B_CATALOGS_MANAGE_ADD_SUCCESS'));

        return;
      }

      await externalClientService.removeProductsFromCatalogs(externalClientIds, productIds);

      showSuccessMessage(i18next.t('ADMIN.PRODUCTS.B2B_CATALOGS_MANAGE_REMOVE_SUCCESS'));
    } catch {
      showErrorMessage(i18next.t('ADMIN.PRODUCTS.B2B_CATALOGS_MANAGE_ERROR'));
    }
  };

  const triggerManageB2bCatalogsModal = async (items) => {
    manageB2bCatalogsProductsRef.current = items || [];

    const defaultMode = getB2bCatalogsUpdateModeChoices()[0];

    setB2bCatalogsSelectedMode(defaultMode);
    setB2bCatalogsSelectedClients([]);

    let clients = [];

    if (b2bCatalogsScopedStoreIds.length) {
      try {
        const { externalClients } = await externalClientService.getPaginatedExternalClients(
          {
            page: 1,
            limit: B2B_CATALOGS_CLIENTS_MAX_PER_PAGE,
            search: '',
            orderBy: 'name',
            orderType: 'asc',
          },
          { storeIds: b2bCatalogsScopedStoreIds },
        );

        clients = externalClients || [];
      } catch {
        showErrorMessage(i18next.t('ADMIN.PRODUCTS.B2B_CATALOGS_MANAGE_CLIENTS_FETCH_ERROR'));

        return;
      }
    }

    setB2bCatalogsClients(clients);
    setIsManageB2bCatalogsModalOpen(true);

    openGenericModal(
      getManageB2bCatalogsModalParams({
        clients,
        selectedClients: [],
        setSelectedClients: setB2bCatalogsSelectedClients,
        selectedMode: defaultMode,
        setSelectedMode: setB2bCatalogsSelectedMode,
        handleSave: handleManageB2bCatalogsSave,
        handleCloseCleanUp: handleManageB2bCatalogsCloseCleanUp,
      }),
    );
  };

  // Refresh the manage-B2B-catalogs modal whenever its selection state changes
  useEffect(() => {
    if (!isManageB2bCatalogsModalOpen) {
      return;
    }

    refreshGenericModal(
      getManageB2bCatalogsModalParams({
        clients: b2bCatalogsClients,
        selectedClients: b2bCatalogsSelectedClients,
        setSelectedClients: setB2bCatalogsSelectedClients,
        selectedMode: b2bCatalogsSelectedMode,
        setSelectedMode: setB2bCatalogsSelectedMode,
        handleSave: handleManageB2bCatalogsSave,
        handleCloseCleanUp: handleManageB2bCatalogsCloseCleanUp,
      }),
    );
  }, [
    isManageB2bCatalogsModalOpen,
    b2bCatalogsClients,
    b2bCatalogsSelectedClients,
    b2bCatalogsSelectedMode,
  ]);
  /**************************/
  /* Delete Product methods */
  /**************************/

  const handleProductsDeletion = (products) => {
    closeGenericModal(); // close confirmation modal

    openModalExportInfo({
      component: DeleteProductsModal,
      products,
      title: i18next.t('ADMIN.PRODUCTS.DELETION', { count: products.length }),
      refreshPage: getProducts,
      isInCentralKitchenOnlyPage,
    });
  };

  /**************************/
  /* Enable Product methods */
  /**************************/

  const handleCallToEnableProduct = async (formattedData) => {
    pageLoading();

    try {
      isInCentralKitchenOnlyPage
        ? await centralService.enableKitchenProductByIds(formattedData)
        : await enableProductMapCashierProductsAndAddProductionParameters(formattedData);

      showSuccessMessage(
        i18next.t(
          isInCentralKitchenOnlyPage
            ? 'ADMIN.PRODUCTS.ENABLE_KITCHEN_PRODUCTS_SUCCESS'
            : 'ADMIN.PRODUCTS.ENABLE_PRODUCT_SUCCESS',
          { count: isInCentralKitchenOnlyPage ? formattedData.length : 0 },
        ),
      );

      await getProducts();
    } catch (err) {
      showErrorMessage(
        i18next.t(
          isInCentralKitchenOnlyPage
            ? 'ADMIN.PRODUCTS.ENABLE_KITCHEN_PRODUCTS_ERROR'
            : 'ADMIN.PRODUCTS.ENABLE_PRODUCT_FAILURE',
          {
            step: err.message,
            count: isInCentralKitchenOnlyPage ? formattedData.length : 0,
          },
        ),
      );
    } finally {
      pageLoaded();
    }
  };

  const handleCallToEnableProducts = async (productIds) => {
    pageLoading();

    try {
      if (isInCentralKitchenOnlyPage) {
        await centralService.enableKitchenProductByIds(productIds);
      } else {
        await productService.enableProductByIds(productIds);
      }

      showSuccessMessage(
        i18next.t(
          isInCentralKitchenOnlyPage
            ? 'ADMIN.PRODUCTS.ENABLE_KITCHEN_PRODUCTS_SUCCESS'
            : 'ADMIN.PRODUCTS.ENABLE_PRODUCTS_SUCCESS',
          { count: productIds.length },
        ),
      );

      await getProducts();
    } catch (err) {
      showErrorMessage(
        i18next.t(
          isInCentralKitchenOnlyPage
            ? 'ADMIN.PRODUCTS.ENABLE_KITCHEN_PRODUCTS_ERROR'
            : 'ADMIN.PRODUCTS.ENABLE_PRODUCTS_ERROR',
          { count: productIds.length },
        ),
      );
    } finally {
      pageLoaded();
    }
  };

  const triggerEnableProductsModal = (products) => {
    const params = getEnableProductsModalParams(
      products,
      handleCallToEnableProducts,
      isInCentralKitchenOnlyPage,
    );

    openGenericModal(params);
  };

  const triggerEnableProductModal = (product) => {
    if (isInCentralKitchenOnlyPage) {
      triggerEnableProductsModal([product]);

      return;
    }

    setEnablingProduct(product);
  };

  const resetEnablingProduct = () => {
    setEnablingProduct(null);
  };

  /**************************/
  /* Disable Product methods */
  /**************************/

  const disableProducts = async (products) => {
    pageLoading();
    const productsIds = products.map(({ id }) => id);

    try {
      isInCentralKitchenOnlyPage
        ? await centralService.disableKitchenProductByIds(productsIds)
        : await productService.disableProductByIds(productsIds);

      showSuccessMessage(
        i18next.t(
          isInCentralKitchenOnlyPage
            ? 'ADMIN.PRODUCTS.DISABLE_KITCHEN_PRODUCTS_SUCCESS'
            : 'ADMIN.PRODUCTS.DISABLE_PRODUCTS_SUCCESS',
          { count: products.length },
        ),
      );

      await getProducts();
    } catch (error) {
      showErrorMessage(
        i18next.t(
          isInCentralKitchenOnlyPage
            ? 'ADMIN.PRODUCTS.DISABLE_KITCHEN_PRODUCTS_ERROR'
            : 'ADMIN.PRODUCTS.DISABLE_PRODUCTS_ERROR',
          { count: products.length },
        ),
      );
    } finally {
      pageLoaded();
    }
  };

  /**************************/
  /* Import Product methods */
  /**************************/

  const getCentralKitchenProductBatchCreationTemplate = async () => {
    pageLoading();

    try {
      const template = await clientService.getCentralKitchenProductBatchCreationTemplate();

      downloadFile(
        template,
        i18next.t('ADMIN.PRODUCTS.DOWNLOAD_TEMPLATE_FOR_CENTRAL_KITCHEN_IMPORT_FILENAME'),
        false,
        false,
      );
    } catch (err) {
      showErrorMessage(i18next.t('ADMIN.PRODUCTS.DOWNLOAD_TEMPLATE_TOASTER_FAILED'));
    } finally {
      pageLoaded();
    }
  };

  const getProductBatchCreationTemplate = async () => {
    pageLoading();

    try {
      const template = await clientService.getProductBatchCreationTemplate();

      downloadFile(template, i18next.t('ADMIN.PRODUCTS.DOWNLOAD_TEMPLATE_FOR_IMPORT_FILENAME'));
    } catch (err) {
      showErrorMessage(i18next.t('ADMIN.PRODUCTS.DOWNLOAD_TEMPLATE_TOASTER_FAILED'));
    } finally {
      pageLoaded();
    }
  };

  const postBatchProductsToCreate = async () => {
    pageLoading();

    try {
      isInCentralKitchenOnlyPage
        ? await clientService.postCentralKitchenProductsBatchCreation(
            clientId,
            batchProductsToCreate,
            importScopeSelectedStores.map(({ id }) => id),
          )
        : await clientService.postProductsBatchCreation(clientId, batchProductsToCreate);

      showSuccessMessage(i18next.t('GENERAL.IMPORT_FILE_BATCH_CREATION_TOASTER_SUCCESS'));
      cleanUpModalImport();

      await getProducts();
    } catch (error) {
      showErrorMessage(i18next.t('GENERAL.IMPORT_FILE_BATCH_CREATION_TOASTER_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const getCentralKitchenProductsBatchFileValidation = async () => {
    pageLoading();

    try {
      const centralKitchenProducts = await formatXlsFileToJson(selectedFile);

      const formattedCentralKitchenProducts =
        formatCentralKitchenProductBatchCreationFile(centralKitchenProducts);
      setBatchProductsToCreate(formattedCentralKitchenProducts);

      await clientService.postCentralKitchenProductsBatchCreationValidate(
        clientId,
        formattedCentralKitchenProducts,
      );

      setCurrentStep(IMPORT_STATE.VALIDATED_FILE);
    } catch (error) {
      if (error.response && error.response.status === 422) {
        setErrorFile(error.response.data);
        setCurrentStep(IMPORT_STATE.ERROR_FILE);
      }
    } finally {
      pageLoaded();
    }
  };

  const getProductsBatchFileValidation = async () => {
    pageLoading();

    try {
      const products = await formatXlsFileToJson(selectedFile);

      const formattedProducts = formatProductBatchUpdateOrCreationFile(
        products,
        clientChannels,
        defaultChannelId,
      );

      setBatchProductsToCreate(formattedProducts);

      await clientService.postProductsBatchCreationValidate(clientId, formattedProducts);

      setCurrentStep(IMPORT_STATE.VALIDATED_FILE);
    } catch (error) {
      if (error.response && error.response.status === 422) {
        setErrorFile(error.response.data);
        setCurrentStep(IMPORT_STATE.ERROR_FILE);
      }
    } finally {
      pageLoaded();
    }
  };

  const handleFileValidation = async () => {
    !isInCentralKitchenOnlyPage
      ? await getProductsBatchFileValidation()
      : await getCentralKitchenProductsBatchFileValidation();
  };

  const handleSwitchStep = (changeStep) => {
    if (changeStep === SWITCH_STEP.PREVIOUS) {
      cleanUpModalImport();
      return;
    }

    if (currentStep === IMPORT_STATE.SELECT_FILE) {
      handleFileValidation();
    }

    if (currentStep === IMPORT_STATE.ERROR_FILE) {
      cleanUpModalImport();
    }

    if (currentStep === IMPORT_STATE.VALIDATED_FILE) {
      postBatchProductsToCreate();
    }
  };

  const handleFileChange = (file) => {
    setSelectedFile(file);
    openProductsImportModal(file);
  };

  const cleanUpModalImport = () => {
    setSelectedFile(null);
    setErrorFile(null);
    setCurrentStep(IMPORT_STATE.SELECT_FILE);
    setBatchProductsToCreate(null);
    handleImportScopeCleanUp();

    closeGenericModal();
  };

  const openProductsImportModal = (file) => {
    const productCreationFile =
      (!!file || file == null) && currentStep === IMPORT_STATE.SELECT_FILE ? file : selectedFile;

    handleOpenedImportFileModal(
      {
        currentStep: currentStep,
        importType: !isInCentralKitchenOnlyPage
          ? IMPORT_TYPE.PRODUCTS
          : IMPORT_TYPE.CENTRAL_KITCHEN_PRODUCTS,
        handleSwitchStep,
        handleFileChange,
        selectedFile: productCreationFile,
        exportErrorData: errorFile,
        cleanImportModal: cleanUpModalImport,
      },
      openGenericModal,
      refreshGenericModal,
      get(modal, 'GenericModalBool', false),
    );
  };

  /*****************************************************/
  /* Import Central Kitchen Products - scope selection */
  /*****************************************************/

  const handleImportScopeCleanUp = () => {
    setIsImportScopeModalOpen(false);
    setImportScopeGroups([]);
    setImportScopeSelectedGroups([]);
    setImportScopeStores([]);
    setImportScopeSelectedStores([]);
  };

  const handleImportScopeNext = () => {
    setIsImportScopeModalOpen(false);
    openProductsImportModal();
  };

  const triggerImportCentralKitchenProductsScope = () => {
    const allGroups = getSelectableGroupsFromStores(centralKitchenStores);
    const initialStores = allGroups.length
      ? getFilteredStoresWithSelectedGroups(allGroups, centralKitchenStores)
      : centralKitchenStores;

    setImportScopeGroups(allGroups);
    setImportScopeSelectedGroups(allGroups);
    setImportScopeStores(initialStores);
    setImportScopeSelectedStores(initialStores);
    setIsImportScopeModalOpen(true);

    openGenericModal(
      getImportCentralKitchenProductsScopeModalConfig({
        groups: allGroups,
        selectedGroups: allGroups,
        setSelectedGroups: setImportScopeSelectedGroups,
        stores: initialStores,
        selectedStores: initialStores,
        setSelectedStores: setImportScopeSelectedStores,
        handleNext: handleImportScopeNext,
        handleCloseCleanUp: handleImportScopeCleanUp,
      }),
    );
  };

  // When selected groups change, all their central kitchens are selected back
  useEffect(() => {
    if (!isImportScopeModalOpen) {
      return;
    }

    const filteredStores = importScopeSelectedGroups.length
      ? getFilteredStoresWithSelectedGroups(importScopeSelectedGroups, centralKitchenStores)
      : centralKitchenStores;

    setImportScopeStores(filteredStores);
    setImportScopeSelectedStores(filteredStores);
  }, [importScopeSelectedGroups, isImportScopeModalOpen, centralKitchenStores]);

  // Refresh the import-scope selection modal whenever its selection state changes
  useEffect(() => {
    if (!isImportScopeModalOpen) {
      return;
    }

    refreshGenericModal(
      getImportCentralKitchenProductsScopeModalConfig({
        groups: importScopeGroups,
        selectedGroups: importScopeSelectedGroups,
        setSelectedGroups: setImportScopeSelectedGroups,
        stores: importScopeStores,
        selectedStores: importScopeSelectedStores,
        setSelectedStores: setImportScopeSelectedStores,
        handleNext: handleImportScopeNext,
        handleCloseCleanUp: handleImportScopeCleanUp,
      }),
    );
  }, [
    isImportScopeModalOpen,
    importScopeGroups,
    importScopeSelectedGroups,
    importScopeStores,
    importScopeSelectedStores,
  ]);

  /**********************************/
  /* Import Product Batch Update methods */
  /**********************************/

  const getProductsBatchUpdateTemplate = async () => {
    pageLoading();

    try {
      const template = await productService.getBatchUpdateTemplateByClientId();

      downloadFile(
        template,
        i18next.t('ADMIN.PRODUCTS.DOWNLOAD_TEMPLATE_FOR_BATCH_UPDATE_FILENAME'),
      );
    } catch (err) {
      showErrorMessage(i18next.t('GENERAL.FILE_DOWNLOAD_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const cleanUpBatchUpdateImport = () => {
    closeGenericModal();
  };

  const openBatchUpdateImportModal = () => {
    const isModalOpen = get(modal, 'GenericModalBool', false);

    if (isModalOpen) {
      return;
    }

    const params = getUpdateProductModalConfig(IMPORT_MODAL_STEPS.SELECT_FILE, {
      handleCloseCleanUp: cleanUpBatchUpdateImport,
      updatedFile: null,
      selectedFile: null,
      handleFileChange: onImportProductUpdateFileChange,
    });

    openGenericModal(params);
  };

  const onImportProductUpdateFileChange = (file) => {
    refreshGenericModal(
      getUpdateProductModalConfig(IMPORT_MODAL_STEPS.VALIDATE_FILE, {
        updatedFile: file,
        handleCloseCleanUp: cleanUpBatchUpdateImport,
        handleFileChange: onImportProductUpdateFileChange,
        handleFileValidation: handleProductUpdateFileValidation,
      }),
    );
  };

  const handleProductUpdateFileValidation = async (file) => {
    pageLoading();

    try {
      const products = await formatXlsFileToJson(file);

      const formattedProducts = formatProductBatchUpdateOrCreationFile(
        products,
        clientChannels,
        defaultChannelId,
        {
          isBatchUpdate: true,
        },
      );

      const { unValidatedProducts } = await productService.validateBatchUpdateTemplate(
        formattedProducts,
      );

      const storeNamePlural = getClientStoreNameTranslation(storeName, true).toLowerCase();

      if (!isEmpty(unValidatedProducts)) {
        handlePartiallyUpdated(file, {
          originalFileData: formattedProducts,
          unValidatedProducts,
        });

        return;
      }

      refreshGenericModal(
        getUpdateProductModalConfig(IMPORT_MODAL_STEPS.VALIDATED_FILE, {
          updatedFile: file,
          storeName: storeNamePlural,
          handleCloseCleanUp: cleanUpBatchUpdateImport,
          handleFileImport: () => handleProductUpdateFileImport(formattedProducts),
        }),
      );
    } catch (error) {
      handleProductUpdateErrorFile(file);
    } finally {
      pageLoaded();
    }
  };

  const handleProductUpdateFileImport = async (originalFileData) => {
    pageLoading();

    try {
      await productService.batchUpdateImport(originalFileData);
      cleanUpBatchUpdateImport();
      await getProducts();

      showSuccessMessage(i18next.t('ADMIN.PRODUCTS.IMPORT_FILE_BATCH_UPDATE_SUCCESS'));
    } catch {
      showErrorMessage(i18next.t('ADMIN.PRODUCTS.IMPORT_FILE_BATCH_UPDATE_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const handlePartiallyUpdated = (file, { originalFileData, unValidatedProducts }) => {
    refreshGenericModal(
      getUpdateProductModalConfig(IMPORT_MODAL_STEPS.PARTIALLY_VALIDATED_FILE, {
        updatedFile: file,
        handleFileChange: onImportProductUpdateFileChange,
        handleCloseCleanUp: cleanUpBatchUpdateImport,
        handleFileImport: () => handleProductUpdateFileImport(originalFileData),
        unValidatedLines: unValidatedProducts,
        errorFile: file,
        exportErrors: () => getImportErrorFileConfig(unValidatedProducts),
      }),
    );
  };

  const handleProductUpdateErrorFile = (file) => {
    refreshGenericModal(
      getUpdateProductModalConfig(IMPORT_MODAL_STEPS.ERROR_FILE, {
        handleCloseCleanUp: cleanUpBatchUpdateImport,
        updatedFile: file,
      }),
    );
  };

  /***************/
  /* Use Effects */
  /***************/

  // Retrieve categories and subcategories
  useEffect(() => {
    fetchProductsCategoriesOfClient(
      setProductsCategories,
      setProductsSubCategories,
      isInCentralKitchenOnlyPage,
    );
  }, []);

  // Build filters
  useEffect(() => {
    setColumnsFilterList(
      getColumnsFilterList(
        columns,
        isInCentralKitchenOnlyPage,
        productsCategories,
        productsSubCategories,
        clientBrands,
      ),
    );
  }, [productsCategories, productsSubCategories, isInCentralKitchenOnlyPage, columns]);

  // Build columns
  useEffect(() => {
    if (isInCentralKitchenOnlyPage) {
      setColumns(getCentralKitchenProductsColumns(storeName));

      return;
    }

    setColumns(getColumns({ areProductsSync, hasMultipleChannels, hasMultipleBrands }));
  }, [isInCentralKitchenOnlyPage]);

  // Build actions
  useEffect(() => {
    const actions = getActions({
      filteringOnActive: get(selectedStatus, 'filterValue', false),
      triggerCreate,
      selectedItems: selectedProducts,
      triggerExport,
      triggerExportByStore,
      hasVariants,
      user,
      openGenericModal,
      handleProductsDeletion,
      disableProducts,
      getProductBatchCreationTemplate,
      getCentralKitchenProductBatchCreationTemplate,
      openProductsImportModal,
      triggerImportCentralKitchenProductsScope,
      triggerEnableProductModal,
      triggerEnableProductsModal,
      isInCentralKitchenOnlyPage,
      hasActiveCentralKitchen: !!centralKitchenStores.length,
      authorizedActions,
      triggerExportIncoData,
      openCategoriesBatchUpdateModal: categoriesBatchUpdateModal.open,
      openSubCategoriesBatchUpdateModal: subCategoriesBatchUpdateModal.open,
      getProductsBatchUpdateTemplate,
      openBatchUpdateImportModal,
      canManageB2bCatalogs,
      triggerManageB2bCatalogsModal,
    });

    setActions(actions);

    const rowActions = getRowActions({
      filteringOnActive: get(selectedStatus, 'filterValue', false),
      triggerExport,
      user,
      openGenericModal,
      handleProductsDeletion,
      disableProducts,
      triggerEnableProductModal,
      isInCentralKitchenOnlyPage,
      openCategoriesBatchUpdateModal: categoriesBatchUpdateModal.open,
      openSubCategoriesBatchUpdateModal: subCategoriesBatchUpdateModal.open,
    });

    setRowActions(rowActions);

    const emptyStateActions = getEmptyStateActions({
      user,
      getProductBatchCreationTemplate,
      getCentralKitchenProductBatchCreationTemplate,
      openProductsImportModal,
      triggerImportCentralKitchenProductsScope,
      isInCentralKitchenOnlyPage,
      hasActiveCentralKitchen: !!centralKitchenStores.length,
      authorizedActions,
      openCategoriesBatchUpdateModal: categoriesBatchUpdateModal.open,
      openSubCategoriesBatchUpdateModal: subCategoriesBatchUpdateModal.open,
    });

    setEmptyStateActions(emptyStateActions);
  }, [products, selectedProducts, selectedStatus, centralKitchenStores]);

  // Handle import file modal
  useEffect(() => {
    if (!selectedFile) {
      return;
    }

    handleOpenedImportFileModal(
      {
        currentStep,
        importType: IMPORT_TYPE.PRODUCTS,
        handleSwitchStep,
        handleFileChange,
        selectedFile: selectedFile,
        exportErrorData: errorFile,
        cleanImportModal: cleanUpModalImport,
      },
      openGenericModal,
      refreshGenericModal,
      get(modal, 'GenericModalBool', false),
    );
  }, [selectedFile, currentStep]);

  // Update data to be equal to the context
  useEffect(() => {
    if (isEmpty(contextHandler)) {
      return;
    }

    // Skip on initial mount if URL already has query params (from refresh or direct URL)
    // But allow context to sync to URL when coming back from detail page (no URL params)
    if (!hasInitializedFromUrl.current && listViewQueryParams[ENUM_QUERY_PARAMS.ORDER_BY]) {
      hasInitializedFromUrl.current = true;
      return;
    }

    const listViewQueryParamsToUpdate = persistentFilters.filter(
      ({ filterType }) => filterType === PersistentFilterTypes.LIST_VIEW,
    );

    listViewQueryParamsToUpdate.forEach(({ contextParam, queryParam }) => {
      if (contextParam && contextParam.length && listViewQueryParams[queryParam] !== contextParam) {
        setListViewQueryParams[queryParam](contextParam);
      }
    });
  }, [
    /*
      We watch every variable instead of only contextHandler because it is refreshed at every change and we don't
      want to enter in this useEffect on every change, only when a state is changed
    */
    !!contextHandler &&
      (contextHandler.search ||
        contextHandler.orderBy ||
        contextHandler.orderType ||
        contextHandler.maxPerPage ||
        contextHandler.currentPage),
  ]);

  useEffect(() => {
    if (
      !listViewQueryParams[ENUM_QUERY_PARAMS.CURRENT_PAGE] &&
      !listViewQueryParams[ENUM_QUERY_PARAMS.ORDER_BY] &&
      !listViewQueryParams[ENUM_QUERY_PARAMS.ORDER_TYPE] &&
      !listViewQueryParams[ENUM_QUERY_PARAMS.MAX_PER_PAGE]
    ) {
      return;
    }

    // Don't fetch if filters are being applied - the applyFilters useEffect will handle it
    if (applyFilters || isEmpty(contextHandler)) {
      return;
    }

    const contextFiltersToUpdate = persistentFilters.filter(
      ({ filterType }) => filterType === PersistentFilterTypes.LIST_VIEW,
    );
    contextFiltersToUpdate.forEach(({ contextParam, updateFunction, queryParam }) => {
      if (
        (!!listViewQueryParams[queryParam] || listViewQueryParams[queryParam] === '') &&
        listViewQueryParams[queryParam] !== contextParam
      ) {
        updateFunction(listViewQueryParams[queryParam]);
      }
    });

    getProducts();
  }, [
    listViewQueryParams[ENUM_QUERY_PARAMS.SEARCH],
    listViewQueryParams[ENUM_QUERY_PARAMS.ORDER_BY],
    listViewQueryParams[ENUM_QUERY_PARAMS.ORDER_TYPE],
    listViewQueryParams[ENUM_QUERY_PARAMS.MAX_PER_PAGE],
    listViewQueryParams[ENUM_QUERY_PARAMS.CURRENT_PAGE],
    customParams,
    get(selectedStatus, 'filterValue', false),
  ]);

  // Set pagination props like SupplierProducts
  useEffect(() => {
    setPaginationProps(
      paginationUtils.getPropsListView({
        listViewRef,
        listViewQueryParams,
        setListViewQueryParams,
        count: totalProducts,
        onQueryParamsChange: () => true, // not used but necessary to adapt select MaxPage ListView
      }),
    );
  }, [totalProducts]);

  // Apply filters
  useEffect(() => {
    if (!applyFilters || !listViewRef || !listViewRef.current) {
      return;
    }

    if ((!advancedFilters || !advancedFilters.length) && applyFilters) {
      listViewRef.current.resetPagination();

      // Trigger refresh of products when filters are reset
      setCustomParams({});
      setListViewQueryParams[ENUM_QUERY_PARAMS.CURRENT_PAGE](1);

      // Reset applyFilters flag
      setApplyFilters(false);

      return;
    }

    const params = advancedFilters.reduce(
      (result, { getQueryParamOperation, propertyKey, value }) => {
        result[propertyKey] = getQueryParamOperation(propertyKey, value);

        return result;
      },
      {},
    );

    // Trigger refresh of products when filters are applied
    setCustomParams(params);
    setListViewQueryParams[ENUM_QUERY_PARAMS.CURRENT_PAGE](1);

    listViewRef.current.resetPagination();

    // Reset applyFilters flag
    setApplyFilters(false);
  }, [advancedFilters, applyFilters]);

  const renderEmptyState = () => (
    <>
      <GeneralEmptyStateListView
        headerActions={emptyStateActions}
        icon={'/images/inpulse/product.svg'}
        renderAction={() => (
          <Button
            color="inpulse-default"
            handleClick={triggerCreate}
            icon={'/images/inpulse/add-white-small.svg'}
            label={i18next.t('ADMIN.PRODUCTS.CREATE')}
          />
        )}
        subtitle={i18next.t('ADMIN.PRODUCTS.EMPTY_STATE_SUBTITLE')}
        title={i18next.t('ADMIN.PRODUCTS.EMPTY_STATE_TITLE')}
      />
    </>
  );

  // Goal is to avoid render ListView with unbuilt pagination props (like SupplierProducts)
  if (!paginationProps) {
    return <></>;
  }

  return (
    <>
      <NavigationBreadCrumb featurePath={path} />
      {emptyState ? (
        renderEmptyState()
      ) : (
        <ListViewContainer>
          <ListView
            {...paginationProps}
            actionOnClick={goProductDetailsPage}
            actions={actions}
            columns={columns}
            data={products}
            defaultSearchInput={search}
            isLoading={isLoading}
            languageCode={userLanguageCode}
            markerConfiguration={{
              isHidden: ({ isChild, isKitchen }) =>
                !hasSimpleModifierManagement || !isChild || isKitchen,
              backgroundColor: theme.colors.brand.secondary,
              icon: { src: '/images/inpulse/add-black-small.svg' },
            }}
            minActionsInActionsDropdown={1}
            padding={'24px 24px 0px 24px'}
            placeholderShape={i18next.t('GENERAL.SEARCH')}
            renderEmptyState={() => <EmptyState label={i18next.t('GENERAL.NO_RESULT')} />}
            renderFilterButton={() => (
              <DeepsightFiltersButton
                advancedFilters={advancedFilters}
                applyFilters={applyFilters}
                columnsFilterList={columnsFilterList}
                customSingleDropDowns={[
                  {
                    id: 'product-state',
                    isDisabled: isUserManager(user) && !userHasRoleInpulse(user),
                    disabledIcon: '/images/inpulse/power-ip-black.svg',
                    itemSelectedIcon: '/images/inpulse/power-ip-black.svg',
                    list: CHOICES_DROPDOWN_ACTIVE(),
                    defaultSelectedItem: CHOICES_DROPDOWN_ACTIVE()[0],
                    selectedItem: selectedStatus,
                    setSelectedItem: setSelectedStatus,
                  },
                ]}
                filters={filters}
                isLoading={isLoading}
                readOnly={isLoading}
                setAdvancedFilters={setAdvancedFilters}
                setApplyFilters={setApplyFilters}
                setFilters={setFilters}
                textFilterButton={i18next.t('GENERAL.LIST_VIEW_FILTER_BUTTON')}
              />
            )}
            rowActions={rowActions}
            setSelectedItems={setSelectedProducts}
          />
        </ListViewContainer>
      )}
      {!!enablingProduct && (
        <EnableProductModal
          handleCallToEnableProduct={handleCallToEnableProduct}
          product={enablingProduct}
          resetEnablingProduct={resetEnablingProduct}
        />
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  modal: state.modalReducer,
  user: state.baseReducer.user,
  client: getClientInfo(state.baseReducer.user),
  currency: state.baseReducer.currency,
  clientBrands: getClientBrandsWithoutNone(state.baseReducer.brands),
  authorizedActions: getAuthorizedActions(state.baseReducer.userRights, '/admin/products/products'),
  activeStores: state.baseReducer.activeStores,
  hasVariants: getHasRecipeVariants(state.baseReducer.userRights),
  canManageB2bCatalogs:
    state.baseReducer.userRights.some(
      ({ path }) => path === EXTERNAL_FEATURE_PATHS.EXTERNAL_CLIENTS,
    ) &&
    (state.baseReducer.user.isDeepsight ||
      canEditExternalClient(
        getAuthorizedActions(state.baseReducer.userRights, EXTERNAL_FEATURE_PATHS.EXTERNAL_CLIENTS),
      )),
});

const mapDispatchToProps = (dispatch) => ({
  openModal: (params) => {
    dispatch(openMediumModal(params));
  },
  openModalExportInfo: (params) => {
    dispatch(openSmallModal(params));
  },
  openGenericModal: (params) => {
    dispatch(openGenericModal(params));
  },
  refreshGenericModal: (params, customPropertyPathsToUpdate) => {
    dispatch(refreshGenericModal(params, customPropertyPathsToUpdate));
  },
  closeGenericModal: (params) => {
    dispatch(closeGenericModal(params));
  },
  showSuccessMessage: (message) => {
    dispatch(showSuccessMessage(message));
  },
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  pageLoading: () => {
    dispatch(loading());
  },
  pageLoaded: () => {
    dispatch(loadingSuccess());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(Products);
